import { SpaOutlined } from '@mui/icons-material';
import { Table, TableBody, TableCell, TableFooter, TableHead, TablePagination, TableRow } from "@mui/material";
import { Info, MapPin, MousePointerClick, Rocket, Repeat } from 'lucide-react';
import { inject, observer } from "mobx-react";
import React, { useState, useCallback, useMemo } from "react";
import { FormattedNumber } from "react-intl";
import { Link } from "react-router-dom";
import { Card } from "randmarcomps";
import { useManufacturer } from "../../contexts/WithManufacturer";
import Path from "../../routes/Path";
import { OrderDetail } from "../../stores/document/Document";
import { RootStore } from '../../stores/rootStore';
import ProductDetailsModal from "../products/ProductDetailsModal";
import CurrencyUtils from "../util/CurrencyUtils";
import FormatDateFromInt from "../util/DateTimeUtil";

interface IProps {
  store?: RootStore;
  productOrders?: OrderDetail[];
  defaultRowsPerPage?: number;
  withSort?: boolean;
  withHeader?: boolean;
  variant?: "default" | "timeline";
}

const OpenOrdersGrid: React.FC<IProps> = (props) => {
  const {
    store,
    productOrders,
    defaultRowsPerPage,
    withSort,
    withHeader,
    variant = "default",
  } = props;
  const { applicationId } = useManufacturer();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage || 500);
  const isTimelineVariant = variant === "timeline";

  const handleChangePage = useCallback((_: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const ordersToDisplay = useMemo(() => {
    if (!productOrders) return [];
    if (withSort) {
      return [...productOrders]
        .sort((a, b) => (a.mpn || "").localeCompare(b.mpn || ""))
        .sort((b, a) => (a.warehouseCode || "").localeCompare(b.warehouseCode || ""))
        .sort((b, a) => (a.orderNumber || "").localeCompare(b.orderNumber || ""));
    }
    return productOrders;
  }, [productOrders, withSort]);

  const { warehousesStore } = store!;

  if (ordersToDisplay.length === 0) {
    return (
      <div className="space-y-4">
        <div className="bg-blue-50 border border-blue-200 text-sm text-blue-800 rounded-lg p-4" role="alert">
          <div className="flex">
            <Info className="flex-shrink-0 h-4 w-4 mt-0.5" />
            <div className="ml-3">
              <h3 className="font-bold text-blue-900">What are Open Orders?</h3>
              <div className="mt-2">
                Once your products are available for resell or consumption, you will be able to monitor all of the open orders here! The reseller name,	order number, purchase order number	(your ERP reference if applicable), order date, Randmar warehouse location, part number, quantity and	unit price	will be shown here.
              </div>
            </div>
          </div>
        </div>

        <Card className="bg-white">
          <div className="p-6">
            <h2 className="text-2xl font-bold">Start Working with Resellers</h2>
            <p className="text-gray-500">Find resellers to open new markets and push your products</p>
          </div>
          <div className="px-6 pb-6">
            <div className="bg-blue-50 border border-blue-200 text-sm text-blue-800 rounded-lg p-4 mb-6" role="alert">
              <div className="flex">
                <Info className="flex-shrink-0 h-4 w-4 mt-0.5" />
                <div className="ml-3">
                  <h3 className="font-bold text-blue-900">Marketing - If Applicable</h3>
                  <div className="mt-2">
                    Note that, with your consent, we will market your brand to our resellers through email and on our social media platforms as well. This will prompt resellers to contact you to Get Started. These will show up as Leads in your Resellers tab.
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 bg-primary/10 text-primary rounded-full h-10 w-10 flex items-center justify-center">
                  <MapPin className="h-5 w-5" />
                </div>
                <div>
                  <h4 className="font-semibold">Locate</h4>
                  <p className="text-gray-500 text-sm">Search for the city you want to target (example: Calgary). A list of resellers will pop up.</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 bg-primary/10 text-primary rounded-full h-10 w-10 flex items-center justify-center">
                  <MousePointerClick className="h-5 w-5" />
                </div>
                <div>
                  <h4 className="font-semibold">Take Action</h4>
                  <p className="text-gray-500 text-sm">Click on the reseller to view their profile or click on the Actions button for quick options.</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 bg-primary/10 text-primary rounded-full h-10 w-10 flex items-center justify-center">
                  <Rocket className="h-5 w-5" />
                </div>
                <div>
                  <h4 className="font-semibold">Start</h4>
                  <p className="text-gray-500 text-sm">Create an Opportunity in order to get the ball rolling!</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 bg-primary/10 text-primary rounded-full h-10 w-10 flex items-center justify-center">
                  <Repeat className="h-5 w-5" />
                </div>
                <div>
                  <h4 className="font-semibold">Repeat</h4>
                  <p className="text-gray-500 text-sm">Start again to expand your market!</p>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <Card className={`w-full ${isTimelineVariant ? "bg-transparent p-0 shadow-none" : "bg-white"}`}>
      <div className={`w-full ${isTimelineVariant ? "overflow-x-auto rounded-[24px] bg-white" : "overflow-auto rounded-[20px]"}`}>
        <Table
          sx={{
            ...(isTimelineVariant && {
              borderCollapse: "separate",
              borderSpacing: 0,
              minWidth: 960,
              "& thead th": {
                backgroundColor: "#f4edff",
                color: "#382456",
                fontFamily: "'Inter', 'Segoe UI', sans-serif",
                fontSize: "0.75rem",
                fontWeight: 600,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                borderBottom: "2px solid #eadcff",
                paddingTop: "18px",
                paddingBottom: "18px",
              },
              "& thead th:first-of-type": {
                borderTopLeftRadius: "18px",
              },
              "& thead th:last-of-type": {
                borderTopRightRadius: "18px",
              },
            }),
          }}
        >
          {withHeader &&
            <TableHead>
              <TableRow>
                <TableCell align="center" sx={{ borderColor: "#582c83" }}>Reseller Name</TableCell>
                <TableCell align="center" sx={{ borderColor: "#582c83" }}>Order Number</TableCell>
                <TableCell align="center" sx={{ borderColor: "#582c83" }}>PO Number</TableCell>
                <TableCell align="center" sx={{ borderColor: "#582c83" }}>MPN</TableCell>
                <TableCell align="center" sx={{ borderColor: "#582c83" }}>Order Date</TableCell>
                <TableCell align="center" sx={{ borderColor: "#582c83" }}>Location</TableCell>
                <TableCell align="center" sx={{ borderColor: "#582c83" }}>Quantity</TableCell>
                <TableCell align="center" sx={{ borderColor: "#582c83" }}>Unit Price</TableCell>
                <TableCell align="center" sx={{ borderColor: "#582c83" }} />
                <TableCell align="center" sx={{ borderColor: "#582c83" }}>({ordersToDisplay.length})</TableCell>
              </TableRow>
            </TableHead>
          }
          <TableBody>
            {ordersToDisplay
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row, index) => (
                <TableRow
                  key={"Order" + index}
                  sx={{
                    backgroundColor: row.getColor() || (isTimelineVariant ? "#fbf9ff" : "transparent"),
                    transition: "background-color 200ms ease",
                    "&:hover": { backgroundColor: isTimelineVariant ? "#f3edff" : "#f9f5ff" },
                    ...(isTimelineVariant && {
                      "& td": {
                        borderBottom: "1px solid #efe4ff",
                        paddingTop: "18px",
                        paddingBottom: "18px",
                      },
                      "&:last-of-type td": {
                        borderBottom: "none",
                      },
                    }),
                  }}
                >
                  <TableCell sx={{ fontWeight: "bold", minWidth: 150 }}><Link to={Path.reseller(applicationId, row.resellerId)}>{row.resellerName}</Link></TableCell>
                  <TableCell><Link to={Path.generalDocument(applicationId, row.orderNumber)} style={{ color: '#363636' }}>{row.orderNumber}</Link></TableCell>
                  <TableCell><Link to={Path.generalDocument(applicationId, row.orderNumber)} style={{ color: '#363636' }}>{row.poNumber}</Link></TableCell>
                  <TableCell align="center" sx={{ fontWeight: "bold", minWidth: 100 }}>
                    <Link to={Path.product(applicationId, row.randmarSKU)}>{row.mpn}</Link>
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 120 }}>{FormatDateFromInt(row.orderDate)}</TableCell>
                  <TableCell align="center">{warehousesStore.getWarehouseNameFromCode(row.warehouseCode)}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: "bold" }}>{row.quantity}</TableCell>
                  <TableCell align="center"><FormattedNumber {...CurrencyUtils.format} value={row.unitPrice || 0} /></TableCell>
                  <TableCell align="center">
                    {row.opportunityManufacTurerId === applicationId &&
                      <Link title={`Opportunity ${row.opportunityManufacturerNumber}`} to={Path.opportunity(applicationId, row.opportunityManufacturerNumber)}><SpaOutlined color="primary" /></Link>
                    }
                  </TableCell>
                  <TableCell align="center"><ProductDetailsModal randmarSKU={row.randmarSKU || ""} /></TableCell>
                </TableRow>
              ))}
          </TableBody>
          {ordersToDisplay.length > rowsPerPage &&
            <TableFooter>
              <TableRow>
                <TablePagination
                  count={ordersToDisplay.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </TableRow>
            </TableFooter>}
        </Table>
      </div>
    </Card>
  );
}

export default inject("store")(observer(OpenOrdersGrid));
